import { useMutation } from "@apollo/client";

import {
  EventsApiResponse,
  UpdateEventResponse,
} from "../../../../common/types/events/api";
import { UpdateEventInput } from "../../../../common/types/events/params";
import {
  errorNotification,
  successNotification,
} from "../../../../common/variables/notification";
import { UPDATE_EVENT } from "../../../../rest/mutations";
import { GET_EVENTS_BY_CUSTOMER } from "../../../../rest/queries";

type GetEventsByCustomerResponse = {
  events: EventsApiResponse;
};

export const useUpdateEvent = () => {
  const [mutate, { data, loading, error }] = useMutation<
    { putEvent: UpdateEventResponse },
    { input: Record<string, string> }
  >(UPDATE_EVENT);

  const updateEvent = async (input: UpdateEventInput) => {
    const { customerId, eventId, ...fieldsToUpdate } = input;

    try {
      const response = await mutate({
        variables: {
          input: {
            pk: customerId,
            sk: eventId,
            ...fieldsToUpdate,
          },
        },
        context: {
          headers: {
            "Content-Type": "application/json",
          },
        },
        onCompleted: () => {
          successNotification("Event updated successfully");
        },
        update: (cache, { data }) => {
          if (!data?.putEvent) return;

          try {
            const customerIdEncoded = encodeURIComponent(customerId);
            const cachedData = cache.readQuery<GetEventsByCustomerResponse>({
              query: GET_EVENTS_BY_CUSTOMER,
              variables: { customerId: customerIdEncoded },
            });

            if (!cachedData || !cachedData.events) return;

            const updatedEvents = {
              ...cachedData.events,
              data: cachedData.events.data.map(item => {
                if (item?.event?.id === eventId) {
                  return {
                    ...item,
                    event: {
                      ...item.event,
                      ...fieldsToUpdate,
                      ddbRecord: {
                        ...item.event.ddbRecord,
                        ...fieldsToUpdate,
                      },
                    },
                  };
                }

                return item;
              }),
            };

            cache.writeQuery({
              query: GET_EVENTS_BY_CUSTOMER,
              variables: { customerId: customerIdEncoded },
              data: { events: updatedEvents },
              overwrite: true,
            });
          } catch (err) {
            console.error("Error updating cache:", err);

            errorNotification();
          }
        },
      });

      return response.data?.putEvent;
    } catch (err) {
      console.error("UpdateEvent:", err);

      errorNotification();

      throw new Error("Failed to update event");
    }
  };

  return {
    updateEvent,
    data: data?.putEvent,
    loading,
    error,
  } as const;
};
